import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const useWebSocket = (url, { token, tenantId } = {}) => {
  const socket = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketAvailable, setSocketAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [activeUsers, setActiveUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const typingTimeoutRef = useRef(new Map());
  const isCleaningUp = useRef(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const reconnectTimer = useRef(null);

  const cleanup = () => {
    isCleaningUp.current = true;
    
    // Clear all typing timeouts
    typingTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
    typingTimeoutRef.current.clear();

    // Clear reconnect timer if it exists
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    if (socket.current) {
      try {
        // Remove all event listeners first
        socket.current.off();
        
        // Only disconnect if socket is connected or connecting
        if (socket.current.connected || socket.current.connecting) {
          socket.current.disconnect();
        }
      } catch (error) {
        // Silently handle cleanup errors - socket might already be closed
      } finally {
        socket.current = null;
      }
    }

    // Reset initialization flag to allow reconnection
    initializingRef.current = false;
    isCleaningUp.current = false;
    reconnectAttempts.current = 0;
  };

  // Track if initialization is in progress to prevent multiple calls
  const initializingRef = useRef(false);

  // Note: Socket.io handles automatic reconnection, so we don't need a manual attemptReconnect function
  // The reconnect() function below is for manual reconnection when needed

  // Initialize Socket.IO connection
  const initializeSocket = useCallback(() => {
    if (!token) {
      return;
    }

    if (!url) {
      return;
    }

    // If socket already exists and is connected, don't create a new one
    if (socket.current && socket.current.connected) {
      setIsConnected(true);
      initializingRef.current = false;
      return;
    }

    // If initialization is already in progress, don't start another one
    if (initializingRef.current) {
      // If it's been stuck for more than 10 seconds, reset it
      setTimeout(() => {
        if (initializingRef.current && (!socket.current || !socket.current.connected)) {
          initializingRef.current = false;
        }
      }, 10000);
      return;
    }

    initializingRef.current = true;

    try {
      // Only cleanup if socket exists and is not connected
      if (socket.current && !socket.current.connected) {
        try {
          socket.current.off();
          // Only disconnect if socket is connecting or has an active connection attempt
          if (socket.current.connecting || socket.current.io) {
            socket.current.disconnect();
          }
        } catch (error) {
          // Silently handle cleanup errors
        } finally {
          socket.current = null;
        }
      }

      // Create Socket.IO instance with auth
      socket.current = io(url, {
        auth: { token, tenantId },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 30000,
        autoConnect: true,
        forceNew: true
      });

      // Connection event handlers
      socket.current.on('connect', () => {
        setIsConnected(true);
        setSocketAvailable(true);
        setError(null);
        reconnectAttempts.current = 0;
        initializingRef.current = false; // Reset initialization flag on successful connection
      });

      socket.current.on('disconnect', (_reason) => {
        setIsConnected(false);
        setActiveUsers(new Set());
        setTypingUsers(new Set());
        initializingRef.current = false;
      });

      socket.current.on('connect_error', (error) => {
        setError('Connection error: ' + (error.message || 'Unknown error'));
        setTimeout(() => {
          initializingRef.current = false;
        }, 2000);
      });

      // Task-specific events with error handling
      socket.current.on('user-joined', ({ userId }) => {
        try {
          setActiveUsers(prev => new Set([...prev, userId]));
        } catch (error) {
        }
      });

      socket.current.on('user-left', ({ userId }) => {
        try {
          setActiveUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
        } catch (error) {
        }
      });

      socket.current.on('user-typing', (data) => {
        try {
          const userId = data?.userId || data;
          if (!userId) {
            return;
          }
          
          if (typingTimeoutRef.current.has(userId)) {
            clearTimeout(typingTimeoutRef.current.get(userId));
          }

          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.add(userId);
            return newSet;
          });

          // Auto-remove typing indicator after 3 seconds of no updates
          typingTimeoutRef.current.set(userId, setTimeout(() => {
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(userId);
              return newSet;
            });
            typingTimeoutRef.current.delete(userId);
          }, 3000));
        } catch (error) {
        }
      });

      socket.current.on('user-typing-stopped', (data) => {
        const userId = data?.userId || data;
        if (!userId) {
          return;
        }
        
        if (typingTimeoutRef.current.has(userId)) {
          clearTimeout(typingTimeoutRef.current.get(userId));
          typingTimeoutRef.current.delete(userId);
        }
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      });

      // Handle errors and closed connections
      socket.current.on('error', (error) => {
        setError(`WebSocket error: ${error.message || 'Unknown error'}`);
      });

      // Try to connect immediately
      if (!socket.current.connected) {
        socket.current.connect();
      }
    } catch (error) {
      setError(`Failed to initialize WebSocket connection: ${error.message}`);
      initializingRef.current = false; // Reset flag on error
    }
  }, [url, token, tenantId]);

  useEffect(() => {
    // Only initialize if we have required credentials
    if (!token || !url) {
      return;
    }

    // Only initialize if not already initializing or connected
    if (!initializingRef.current && (!socket.current || !socket.current.connected)) {
      initializeSocket();
    } else if (socket.current && !socket.current.connected && !initializingRef.current) {
      initializeSocket();
    }

    // Cleanup on unmount only - don't cleanup on dependency changes
    return () => {
      if (socket.current && !isCleaningUp.current) {
        cleanup();
      }
    };
  }, [url, token, tenantId, initializeSocket]);

  // Join a task room
  const joinTask = useCallback((taskId) => {
    if (socket.current?.connected) {
      socket.current.emit('join-task', taskId);
    } else if (socket.current) {
      const onConnect = () => {
        socket.current.emit('join-task', taskId);
        socket.current.off('connect', onConnect);
      };
      socket.current.on('connect', onConnect);
    }
  }, []);

  // Leave a task room
  const leaveTask = useCallback((taskId) => {
    if (socket.current?.connected) {
      socket.current.emit('leave-task', taskId);
    }
  }, []);

  // Join task list room
  const joinTaskList = useCallback((tenantId) => {
    if (socket.current?.connected) {
      socket.current.emit('join-task-list', tenantId);
    } else if (socket.current) {
      const onConnect = () => {
        socket.current.emit('join-task-list', tenantId);
        socket.current.off('connect', onConnect);
      };
      socket.current.on('connect', onConnect);
    }
  }, []);

  // Leave task list room
  const leaveTaskList = useCallback((tenantId) => {
    if (socket.current?.connected) {
      socket.current.emit('leave-task-list', tenantId);
    }
  }, []);

  // Send typing indicators with debounce
  const sendTypingStart = useCallback((taskId) => {
    if (socket.current?.connected) {
      socket.current.emit('typing-start', taskId);
    }
  }, []);

  const sendTypingStop = useCallback((taskId) => {
    if (socket.current?.connected) {
      socket.current.emit('typing-stop', taskId);
    }
  }, []);

  // Mark message as seen
  const markMessageSeen = useCallback((taskId, messageId) => {
    if (socket.current?.connected) {
      socket.current.emit('message-seen', { taskId, messageId });
    }
  }, []);

  // Subscribe to events
  const subscribe = useCallback((event, callback) => {
    if (socket.current) {
      socket.current.on(event, callback);
      return () => {
        if (socket.current) {
          socket.current.off(event, callback);
        }
      };
    }
    // Socket not available yet - this is normal during initialization
    // Return empty cleanup function
    return () => {};
  }, []);

  // Manual reconnect function that can be called externally
  const reconnect = useCallback(() => {
    cleanup();
    reconnectAttempts.current = 0;
    initializingRef.current = false;
    setTimeout(() => {
      initializeSocket();
    }, 100);
  }, [initializeSocket]);

  return {
    socket: socket.current,
    isConnected,
    socketAvailable,
    error,
    activeUsers,
    typingUsers,
    joinTask,
    leaveTask,
    joinTaskList,
    leaveTaskList,
    sendTypingStart,
    sendTypingStop,
    markMessageSeen,
    subscribe,
    reconnect
  };
};

export default useWebSocket; 